import { SERVER_ACTION_SKEW_PATTERN } from '@/lib/observability/server-action-skew'

/**
 * sentry-noise-filters.ts — the `ignoreErrors` patterns shared by `sentry.server.config.ts` and
 * `sentry.edge.config.ts`.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────────────────────
 *
 * Both configs install `Sentry.captureConsoleIntegration({ levels: ['error'] })`, and that
 * integration is load-bearing: Next.js 16's `cacheComponents` validation errors (a `cookies()`
 * call inside a `'use cache'` body, say) are thrown as an internal `StaticGenBailoutError` outside
 * React's render stream, so `onRequestError` never fires and `console.error` is the only place the
 * real Error surfaces. See `sentry.server.config.ts`'s own comment.
 *
 * The cost of that reach is that it captures **every** `console.error`, including output that is
 * not ours and is not an error. Node writes its process warnings — `ExperimentalWarning`,
 * `DeprecationWarning`, `MaxListenersExceededWarning` — in a fixed `(node:<pid>) XxxWarning: …`
 * format, and under Next/Turbopack they reach the console error channel and therefore Sentry, at
 * `level: error`.
 *
 * One of them, `vm.USE_MAIN_CONTEXT_DEFAULT_LOADER is an experimental feature`, is Sentry
 * RANKSURF-5B: **5,808 events since 2026-05-04** — more than every genuine error in the project
 * combined, several times over. It is emitted once per cold start by Node itself, about a Node
 * feature flag this codebase does not set and cannot influence, and there has never been an action
 * anyone could take on it. It also displaced real signal: an issue list sorted by frequency puts a
 * Node startup notice above every production defect on it, and the error budget it consumes is the
 * budget alerting is calibrated against.
 *
 * ── Why a pattern rather than one literal ────────────────────────────────────────────────────
 *
 * Matching only this one warning's text would leave the next Node warning to repeat the whole
 * incident — the class is what is unactionable, not the instance. The `(node:<pid>) XxxWarning:`
 * prefix is emitted by Node's own `process.emitWarning` formatting and cannot collide with an
 * application message, so the pattern stays tightly anchored to the start of the string.
 *
 * Note what is deliberately NOT filtered: nothing thrown by application code, and no other console
 * output at all. A Node warning that ever does become actionable is still on stderr and still in
 * the platform logs — this only stops it being an *alert*.
 */

/**
 * Anchored at the start of the message: Node's process-warning prefix, e.g.
 * `(node:4) ExperimentalWarning: …`, `(node:12) DeprecationWarning: …`.
 *
 * Sentry's inbound filter tests `ignoreErrors` against an event's message as well as its exception
 * values, which is what makes a regex the right tool for a `captureConsoleIntegration` event —
 * those carry a message and no exception.
 */
export const NODE_PROCESS_WARNING_PATTERN = /^\(node:\d+\)\s+\w*Warning:/

/**
 * Sentry RANKSURF-8G: deploy skew — a tab on an old deployment calls a Server Action id the new one
 * no longer has. Expected on every deploy and handled for the user (every error boundary shows a
 * reload prompt), so it is not an alert. One regex, `SERVER_ACTION_SKEW_PATTERN`, covers both the
 * server's message and the browser's `UnrecognizedActionError`.
 */

/** Passed verbatim as `Sentry.init({ ignoreErrors })` in both server-side configs. */
export const SENTRY_IGNORE_ERRORS: Array<string | RegExp> = [
  NODE_PROCESS_WARNING_PATTERN,
  SERVER_ACTION_SKEW_PATTERN,
]

/**
 * Sentry RANKSURF-8S: browser-extension errors (MetaMask injects `inpage.js` into every page and
 * throws when no wallet is connected). Nothing in this codebase talks to a wallet, so none of these
 * can be ours. Message patterns are deliberately specific to the extension's own wording.
 */
export const BROWSER_EXTENSION_ERROR_PATTERNS: RegExp[] = [/Failed to connect to MetaMask/]

/** Passed verbatim as `Sentry.init({ ignoreErrors })` in `sentry.client.config.ts`. */
export const SENTRY_CLIENT_IGNORE_ERRORS: Array<string | RegExp> = [
  SERVER_ACTION_SKEW_PATTERN,
  ...BROWSER_EXTENSION_ERROR_PATTERNS,
]
